import { atom, useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useInstanceId } from 'shared/src/hooks';
import { isAndroid } from 'shared/src/utils';
import type { OptionComponent } from 'shared/src/api';
import { useGoodsRoute } from '../../../hooks/useGoodsRoute';

type SelectedOptions = { [key: number]: OptionComponent };
type SelectedOptionsUpdater =
  | SelectedOptions
  | ((prev: SelectedOptions) => SelectedOptions);

const selectedOptionsAtom = atom<Record<string, SelectedOptions>>({});

export const useSelectedOptions = () => {
  const routes = useGoodsRoute<'GoodsDetail'>();
  const instanceId = useInstanceId();

  const key = `${routes.key}-${instanceId}`;
  const derivedAtom = useMemo(
    () =>
      atom(
        get => get(selectedOptionsAtom)[key] ?? {},
        (get, set, next: SelectedOptionsUpdater) => {
          const current = get(selectedOptionsAtom)[key] ?? {};
          const value = typeof next === 'function' ? next(current) : next;
          set(selectedOptionsAtom, prev => ({ ...prev, [key]: value }));
        },
      ),
    [key],
  );
  const [value, setValue] = useAtom(derivedAtom);

  useEffect(() => {
    if (!isAndroid) {
      setValue({});
    }
  }, [setValue]);

  return [value, setValue] as const;
};
